import { useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import {
  useListCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  getListCategoriesQueryKey,
  Category,
  ApiError,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, Plus, Check, X, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const QUERY_KEY = getListCategoriesQueryKey();

export default function StaffCategoriesPage() {
  const { data: categories, isLoading } = useListCategories();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const handleCreate = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    createCategory.mutate(
      { data: { name: trimmed } },
      {
        onSuccess: () => {
          toast({ title: "Category created" });
          setNewName("");
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
        onError: (err: unknown) => {
          const msg =
            err instanceof ApiError && err.status === 409
              ? "A category with that name already exists"
              : "Failed to create category";
          toast({ title: msg, variant: "destructive" });
        },
      }
    );
  };

  const startEdit = (cat: Category) => {
    setEditingId(cat.id);
    setEditingName(cat.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleRename = (id: number) => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    updateCategory.mutate(
      { id, data: { name: trimmed } },
      {
        onSuccess: () => {
          toast({ title: "Category renamed" });
          setEditingId(null);
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
        onError: (err: unknown) => {
          const msg =
            err instanceof ApiError && err.status === 409
              ? "A category with that name already exists"
              : "Failed to rename category";
          toast({ title: msg, variant: "destructive" });
        },
      }
    );
  };

  const handleToggleActive = (cat: Category) => {
    updateCategory.mutate(
      { id: cat.id, data: { name: cat.name, isActive: !cat.isActive } },
      {
        onSuccess: () => {
          toast({
            title: cat.isActive ? "Category deactivated" : "Category activated",
            description: cat.isActive
              ? "Hidden from new-record dropdowns. Existing issues keep this category."
              : "Available again in new-record dropdowns.",
          });
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
        onError: () => toast({ title: "Failed to update", variant: "destructive" }),
      }
    );
  };

  const handleDelete = (cat: Category) => {
    if (!confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
    deleteCategory.mutate(
      { id: cat.id },
      {
        onSuccess: () => {
          toast({ title: "Category deleted" });
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
        onError: (err: unknown) => {
          const msg =
            err instanceof ApiError && err.status === 409
              ? `Cannot delete "${cat.name}" — it is still used by existing issues or entries`
              : "Failed to delete category";
          toast({ title: msg, variant: "destructive" });
        },
      }
    );
  };

  return (
    <StaffLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Categories</h1>
          <p className="text-slate-500 text-sm">
            Manage the categories used to classify Known Issues and What's Changed entries.
            Deactivate to hide from new-record dropdowns without affecting existing records.
          </p>
        </div>
      </div>

      <div className="grid gap-6 max-w-2xl">
        <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="w-4 h-4 text-primary" />
              Add New Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. Authentication, MCS-150, UCR Registration"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
                className="flex-1"
              />
              <Button
                onClick={handleCreate}
                disabled={!newName.trim() || createCategory.isPending}
                className="bg-primary hover:bg-orange-600"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="w-4 h-4 text-slate-400" />
              All Categories
              {categories && (
                <Badge variant="secondary" className="ml-1">
                  {categories.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : categories?.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm">
                No categories yet. Add one above.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {categories?.map((cat) => (
                  <li
                    key={cat.id}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    {editingId === cat.id ? (
                      <>
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename(cat.id);
                            if (e.key === "Escape") cancelEdit();
                          }}
                          className="flex-1 h-8 text-sm"
                          autoFocus
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleRename(cat.id)}
                          disabled={updateCategory.isPending}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-400 hover:text-slate-600"
                          onClick={cancelEdit}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className={`flex-1 text-sm font-medium ${cat.isActive ? "text-slate-800 dark:text-slate-200" : "text-slate-400 dark:text-slate-500 line-through"}`}>
                          {cat.name}
                        </span>
                        <div className="flex items-center gap-2 mr-2">
                          <Switch
                            checked={cat.isActive}
                            onCheckedChange={() => handleToggleActive(cat)}
                            disabled={updateCategory.isPending}
                            aria-label={cat.isActive ? "Deactivate" : "Activate"}
                          />
                          <span className="text-xs text-slate-400 hidden sm:inline">
                            {cat.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-400 hover:text-primary"
                          onClick={() => startEdit(cat)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-400 hover:text-destructive"
                          onClick={() => handleDelete(cat)}
                          title="Delete (or use the toggle to deactivate)"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </StaffLayout>
  );
}
